import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { QuoteDrawer } from './QuoteDrawer';
import { QuotesList } from './QuotesList';
import { QuoteWizard } from './QuoteWizard';
import {
  useCustomersForQuote,
  useLeadsForQuote,
  useQuotes,
} from './quotesQueries';
import styles from './quotes.module.css';

export function QuotesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: quotes = [], isLoading, error } = useQuotes(orgId);
  const { data: customers = [] } = useCustomersForQuote(orgId);
  const { data: leads = [] } = useLeadsForQuote(orgId);

  const [params, setParams] = useSearchParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  // Selection is by id, not by row: `?id=` links arrive before the list has
  // loaded, and a just-created quote is not in the list until it refetches.
  // Holding the id lets the drawer open as soon as the row exists.
  const [selectedId, setSelectedId] = useState<string | null>(params.get('id'));
  const selectedQuote = quotes.find((q) => q.id === selectedId) ?? null;

  const paramId = params.get('id');
  useEffect(() => {
    if (paramId) setSelectedId(paramId);
  }, [paramId]);

  // `?new=1` opens the wizard (Ctrl+K "צור הצעת מחיר").
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setWizardOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [wantsNew, params, setParams]);

  function closeDrawer() {
    setSelectedId(null);
    if (params.has('id')) {
      const next = new URLSearchParams(params);
      next.delete('id');
      setParams(next, { replace: true });
    }
  }

  function handleCreated(quoteId: string) {
    setWizardOpen(false);
    setSelectedId(quoteId);
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1>הצעות מחיר</h1>
          <p className={styles.pageSub}>שלח הצעות ללקוח, קבל אישור — הכל בקישור אחד.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={() => { setWizardOpen(true); }}>
          <Plus size={16} aria-hidden="true" />
          הצעה חדשה
        </button>
      </div>

      {isLoading ? <p className={styles.notice}>טוען הצעות...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון הצעות · נסה שוב</p> : null}

      {!isLoading && !error && quotes.length === 0 ? (
        <div className={styles.empty}>
          <p>אין הצעות עדיין. צור את הראשונה בלחיצה על «הצעה חדשה».</p>
        </div>
      ) : null}

      {!isLoading && !error && quotes.length > 0 ? (
        <QuotesList
          quotes={quotes}
          customers={customers}
          leads={leads}
          onSelect={(q) => { setSelectedId(q.id); }}
        />
      ) : null}

      <QuoteWizard
        open={wizardOpen}
        orgId={orgId}
        userId={userId}
        onClose={() => { setWizardOpen(false); }}
        onCreated={handleCreated}
      />

      <QuoteDrawer
        open={selectedQuote !== null}
        quote={selectedQuote}
        orgId={orgId}
        userId={userId}
        customers={customers}
        leads={leads}
        onClose={closeDrawer}
      />
    </div>
  );
}
